import { useDraggable } from '@dnd-kit/core';
import {
  Tag,
  TagLabel,
  TagCloseButton,
  Badge,
  useColorModeValue,
} from '@chakra-ui/react';

export interface AssignedDriverDragData {
  driverId: number;
  driverName: string;
  sourceVehicleId: number;
  sourceZone: 'principal' | 'secondary' | 'occasional' | 'learners';
}

interface DraggableAssignedDriverChipProps {
  driverId: number;
  name: string;
  vehicleId: number;
  zone: 'principal' | 'secondary' | 'occasional' | 'learners';
  onRemove: () => void;
  isRemovable?: boolean;
  licenseClass?: string;
  isExcluded?: boolean;
}

const DraggableAssignedDriverChip = ({
  driverId,
  name,
  vehicleId,
  zone,
  onRemove,
  isRemovable = true,
  licenseClass,
  isExcluded = false,
}: DraggableAssignedDriverChipProps) => {
  const dragData: AssignedDriverDragData = {
    driverId,
    driverName: name,
    sourceVehicleId: vehicleId,
    sourceZone: zone,
  };

  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `assigned-${vehicleId}-${zone}-${driverId}`,
    data: { assignedDriver: dragData },
    disabled: isExcluded,
  });

  const normalBg = useColorModeValue('blue.50', 'blue.900');
  const normalBorder = useColorModeValue('blue.200', 'blue.700');
  const excludedBg = useColorModeValue('red.50', 'red.900');
  const excludedBorder = useColorModeValue('red.300', 'red.700');

  const bgColor = isExcluded ? excludedBg : normalBg;
  const borderColor = isExcluded ? excludedBorder : normalBorder;

  return (
    <Tag
      ref={setNodeRef}
      size="md"
      variant="subtle"
      bg={bgColor}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="md"
      px={2}
      py={1}
      cursor={isExcluded ? 'default' : 'grab'}
      opacity={isDragging ? 0.5 : 1}
      {...listeners}
      {...attributes}
    >
      <TagLabel fontSize="sm" fontWeight="500">
        {name}
      </TagLabel>
      {licenseClass && !isExcluded && (
        <Badge ml={1} colorScheme="gray" fontSize="2xs" variant="subtle">
          {licenseClass}
        </Badge>
      )}
      {isRemovable && (
        <TagCloseButton
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          onPointerDown={(e) => e.stopPropagation()}
        />
      )}
    </Tag>
  );
};

export default DraggableAssignedDriverChip;
